/**
 * useDraftPoolFetch - once-per-mount /api/players fetch for the live-draft
 * pool wrappers (Auction + Snake) with bounded auto-retry and a visible
 * failure state.
 *
 * [Egress fix 2026-07-31, greenlit] Live-draft pools previously refetched
 * the full pool on every sale/pick, keyed on the sale counter. That is
 * pure wasted Supabase egress - availability is derived client-side from
 * draftState.managers via draftedById, and DraftContext pushes full
 * state on every SALE_COMPLETE / SALE_UNDONE / STATE_SNAPSHOT over the
 * WebSocket. The static pool never changes mid-draft.
 *
 * Dropping the sale-counter dep also removed the accidental self-heal
 * where a failed fetch would silently retry on the next sale. Post-fix
 * the fetch fires exactly once per mount, so a single failed initial
 * load = empty pool for the whole draft = a manager locked out of
 * nominating in a live auction. NOT acceptable. This hook adds:
 *
 *   1. Bounded auto-retry on failure (3 attempts, backoff 500 / 1500 /
 *      3000 ms). Steady-state success stays at ONE network GET / mount.
 *   2. A `loadError` + `retry()` return so the caller can render a
 *      visible error banner with a Retry button.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { authHeaders } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RETRY_DELAYS_MS = [500, 1500, 3000];

export function useDraftPoolFetch(leagueId) {
  const [players, setPlayers] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manualRetryCount, setManualRetryCount] = useState(0);
  const aliveRef = useRef(true);

  const doFetch = useCallback(async () => {
    if (!leagueId) return;
    aliveRef.current = true;
    setLoading(true);
    setLoadError(null);
    let lastErr = null;
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
      if (!aliveRef.current) return;
      try {
        const { data } = await axios.get(`${API}/players`, {
          params: { league_id: leagueId, available_only: false, limit: 2500 },
          headers: authHeaders(),
        });
        if (!aliveRef.current) return;
        setPlayers(data || []);
        setLoadError(null);
        setLoading(false);
        return;
      } catch (err) {
        lastErr = err;
        if (attempt < RETRY_DELAYS_MS.length) {
          await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
        }
      }
    }
    if (!aliveRef.current) return;
    setLoadError(
      lastErr?.response?.data?.detail ||
      lastErr?.message ||
      "Failed to load player pool"
    );
    setLoading(false);
  }, [leagueId]);

  useEffect(() => {
    aliveRef.current = true;
    doFetch();
    return () => { aliveRef.current = false; };
    // Dependency intentionally KEYED ONLY ON leagueId (+ manualRetryCount).
    // Do NOT reintroduce sale/pick counters here - that reverts the whole
    // point of the egress fix.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, manualRetryCount]);

  const retry = useCallback(() => setManualRetryCount((n) => n + 1), []);

  return { players, loadError, loading, retry };
}
